import { useState, useMemo } from 'react';
import { ArrowRight, Bolt } from 'lucide-react';

import AISolutions from "@/assets/images/capabilities/ai-solutions.webp";
import CloudSolutions from "@/assets/images/capabilities/cloud-solution.webp";
import CustomSoftwareDevelopment from "@/assets/images/capabilities/website-development-1.webp";
import EcommerceSolutions from "@/assets/images/capabilities/e-commerce.webp";
import WebHosting from "@/assets/images/capabilities/webhosting.webp";
import ITInfrastructureManagement from "@/assets/images/capabilities/it-service.webp";
import SEO from "@/assets/images/domain/simpleSeo.svg";
import Cybersecurity from "@/assets/images/capabilities/cyber-security.webp";
import UIUX from "@/assets/images/capabilities/ui-ux.webp";
import NASSolutions from "@/assets/images/capabilities/Nas-breadcrumbs.webp";
import ITConsulting from "@/assets/images/capabilities/IT-Consulting.webp";
import VoipService from "@/assets/images/capabilities/3CX.webp";

import { useNavigate } from 'react-router-dom';

/* ─── Each theme includes a dark-but-tinted bgBase ──────── */
const CARD_THEMES = [
  { gradientFrom: '#818CF8', gradientTo: '#38BDF8', bar: 'linear-gradient(90deg, #818CF8, #38BDF8)', bgBase: '#09090f' },
  { gradientFrom: '#38BDF8', gradientTo: '#34D399', bar: 'linear-gradient(90deg, #38BDF8, #34D399)', bgBase: '#060d0b' },
  { gradientFrom: '#34D399', gradientTo: '#818CF8', bar: 'linear-gradient(90deg, #34D399, #818CF8)', bgBase: '#060d09' },
  { gradientFrom: '#6366F1', gradientTo: '#6EE7B7', bar: 'linear-gradient(90deg, #6366F1, #6EE7B7)', bgBase: '#09090f' },
  { gradientFrom: '#7DD3FC', gradientTo: '#A5B4FC', bar: 'linear-gradient(90deg, #7DD3FC, #A5B4FC)', bgBase: '#070c10' },
  { gradientFrom: '#10B981', gradientTo: '#0EA5E9', bar: 'linear-gradient(90deg, #10B981, #0EA5E9)', bgBase: '#060d09' },
  { gradientFrom: '#38BDF8', gradientTo: '#818CF8', bar: 'linear-gradient(90deg, #38BDF8, #818CF8, #34D399)', bgBase: '#070b10' },
  { gradientFrom: '#4F46E5', gradientTo: '#7DD3FC', bar: 'linear-gradient(90deg, #4F46E5, #7DD3FC)', bgBase: '#07070f' },
  { gradientFrom: '#6366F1', gradientTo: '#6EE7B7', bar: 'linear-gradient(90deg, #6366F1, #6EE7B7)', bgBase: '#09090f' },
  { gradientFrom: '#F59E0B', gradientTo: '#818CF8', bar: 'linear-gradient(90deg, #F59E0B, #818CF8)', bgBase: '#0e0b05' },
  { gradientFrom: '#14B8A6', gradientTo: '#38BDF8', bar: 'linear-gradient(90deg, #14B8A6, #38BDF8)', bgBase: '#050d0c' },
  { gradientFrom: '#8B5CF6', gradientTo: '#38BDF8', bar: 'linear-gradient(90deg, #8B5CF6, #38BDF8)', bgBase: '#09060f' },
];

/* ─── Animated SVG Icons ─────────────────────────────────── */
const AIIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="ai-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M26 10 C20 10 14 14 14 20 C14 24 16 27 20 28 L20 38 L32 38 L32 28 C36 27 38 24 38 20 C38 14 32 10 26 10Z"
      stroke="url(#ai-g)" strokeWidth="1.5" fill="none" strokeLinecap="round">
      <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="2.5s" repeatCount="indefinite" />
    </path>
    {([[22,22],[26,18],[30,22],[22,30],[30,30]] as [number,number][]).map(([cx,cy],i) => (
      <circle key={i} cx={cx} cy={cy} r="2" fill="url(#ai-g)">
        <animate attributeName="r" values="2;3;2" dur="1.5s" begin={`${i*0.3}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" begin={`${i*0.3}s`} repeatCount="indefinite" />
      </circle>
    ))}
    <line x1="22" y1="22" x2="26" y2="18" stroke="url(#ai-g)" strokeWidth="1">
      <animate attributeName="opacity" values="0.2;0.8;0.2" dur="2s" repeatCount="indefinite" />
    </line>
    <line x1="26" y1="18" x2="30" y2="22" stroke="url(#ai-g)" strokeWidth="1">
      <animate attributeName="opacity" values="0.8;0.2;0.8" dur="2s" repeatCount="indefinite" />
    </line>
    <line x1="22" y1="22" x2="22" y2="30" stroke="url(#ai-g)" strokeWidth="1">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="1.8s" begin="0.3s" repeatCount="indefinite" />
    </line>
    <line x1="30" y1="22" x2="30" y2="30" stroke="url(#ai-g)" strokeWidth="1">
      <animate attributeName="opacity" values="1;0.3;1" dur="1.8s" begin="0.6s" repeatCount="indefinite" />
    </line>
  </svg>
);

const CloudIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="cl-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M14 30 C10 30 8 27 8 24 C8 21 10 18 14 18 C14 14 17 11 21 11 C23 11 25 12 26 14 C27 12 29 11 32 11 C36 11 39 14 39 18 C42 18 44 21 44 24 C44 27 42 30 38 30Z"
      stroke="url(#cl-g)" strokeWidth="1.5" fill="none" strokeLinecap="round">
      <animate attributeName="d"
        values="M14 30 C10 30 8 27 8 24 C8 21 10 18 14 18 C14 14 17 11 21 11 C23 11 25 12 26 14 C27 12 29 11 32 11 C36 11 39 14 39 18 C42 18 44 21 44 24 C44 27 42 30 38 30Z;M14 31 C9 31 7 28 7 25 C7 22 9 18 14 18 C14 13 17 10 22 10 C24 10 26 11 27 13 C28 11 30 10 33 10 C37 10 40 13 40 18 C43 18 45 21 45 24 C45 27 43 31 39 31Z;M14 30 C10 30 8 27 8 24 C8 21 10 18 14 18 C14 14 17 11 21 11 C23 11 25 12 26 14 C27 12 29 11 32 11 C36 11 39 14 39 18 C42 18 44 21 44 24 C44 27 42 30 38 30Z"
        dur="4s" repeatCount="indefinite" />
    </path>
    {([[18,36],[26,38],[34,36],[22,40],[30,40]] as [number,number][]).map(([cx,cy],i) => (
      <line key={i} x1={cx} y1={cy} x2={cx-1} y2={cy+5} stroke="url(#cl-g)" strokeWidth="1.5" strokeLinecap="round">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" begin={`${i*0.25}s`} repeatCount="indefinite" />
        <animateTransform attributeName="transform" type="translate" values="0 0;0 4;0 0" dur="1.5s" begin={`${i*0.25}s`} repeatCount="indefinite" />
      </line>
    ))}
  </svg>
);

const CodeIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="co-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M20 18 L12 26 L20 34" stroke="url(#co-g)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <animate attributeName="d" values="M20 18 L12 26 L20 34;M18 18 L8 26 L18 34;M20 18 L12 26 L20 34" dur="2s" repeatCount="indefinite" />
    </path>
    <path d="M32 18 L40 26 L32 34" stroke="url(#co-g)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <animate attributeName="d" values="M32 18 L40 26 L32 34;M34 18 L44 26 L34 34;M32 18 L40 26 L32 34" dur="2s" repeatCount="indefinite" />
    </path>
    <line x1="28" y1="16" x2="24" y2="36" stroke="url(#co-g)" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
    </line>
  </svg>
);

const CartIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="ca-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M10 12 L14 12 L18 30 L36 30 L40 18 L16 18" stroke="url(#ca-g)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    {([20,33] as number[]).map((cx,i) => (
      <circle key={i} cx={cx} cy={35} r="2.5" stroke="url(#ca-g)" strokeWidth="1.5" fill="none">
        <animate attributeName="r" values="2.5;3.5;2.5" dur="1.5s" begin={`${i*0.4}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {([[38,12],[42,10],[40,16]] as [number,number][]).map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="1.5" fill="url(#ca-g)">
        <animate attributeName="opacity" values="0;1;0" dur="1.2s" begin={`${i*0.3}s`} repeatCount="indefinite" />
        <animate attributeName="r" values="0;2;0" dur="1.2s" begin={`${i*0.3}s`} repeatCount="indefinite" />
      </circle>
    ))}
  </svg>
);

const TargetIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="ta-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    {([14,10,6] as number[]).map((r,i) => (
      <circle key={i} cx="26" cy="26" r={r} stroke="url(#ta-g)" strokeWidth="1.5" fill="none" opacity={0.4+i*0.25}>
        <animate attributeName="r" values={`${r};${r+1.5};${r}`} dur="2s" begin={`${i*0.4}s`} repeatCount="indefinite" />
      </circle>
    ))}
    <circle cx="26" cy="26" r="3" fill="url(#ta-g)">
      <animate attributeName="r" values="3;4;3" dur="1s" repeatCount="indefinite" />
    </circle>
    <line x1="34" y1="18" x2="28" y2="24" stroke="url(#ta-g)" strokeWidth="1.8" strokeLinecap="round">
      <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
    </line>
    <path d="M34 18 L30 18 L34 22" stroke="url(#ta-g)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const ServerIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="sv-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    {([[12,13],[12,23],[12,33]] as [number,number][]).map(([x,y],i) => (
      <g key={i}>
        <rect x={x} y={y} width="28" height="8" rx="2" stroke="url(#sv-g)" strokeWidth="1.5" fill="none" />
        <circle cx="35" cy={y+4} r="2" fill="url(#sv-g)">
          <animate attributeName="fill" values={`${c1};${c2};${c1}`} dur="1.5s" begin={`${i*0.4}s`} repeatCount="indefinite" />
          <animate attributeName="r" values="2;2.8;2" dur="1.5s" begin={`${i*0.4}s`} repeatCount="indefinite" />
        </circle>
        <line x1="16" y1={y+4} x2="16" y2={y+4} stroke="url(#sv-g)" strokeWidth="1" opacity="0.5">
          <animate attributeName="x2" values="16;28;16" dur={`${1.5+i*0.2}s`} begin={`${i*0.3}s`} repeatCount="indefinite" />
        </line>
      </g>
    ))}
  </svg>
);

const PaletteIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="pa-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M26 10 C18 10 11 16 11 24 C11 32 17 38 24 38 C26 38 28 36 28 34 C28 32 29 31 31 31 C33 31 35 32 37 32 C40 32 42 30 42 27 C42 18 35 10 26 10Z"
      stroke="url(#pa-g)" strokeWidth="1.5" fill="none" />
    {([[19,20],[26,16],[33,20],[36,28]] as [number,number][]).map(([cx,cy],i) => (
      <circle key={i} cx={cx} cy={cy} r="3" fill="url(#pa-g)" opacity="0.7">
        <animate attributeName="r" values="3;4.5;3" dur="2s" begin={`${i*0.4}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" begin={`${i*0.4}s`} repeatCount="indefinite" />
      </circle>
    ))}
    <g style={{ transformOrigin: '36px 40px' }}>
      <animateTransform attributeName="transform" type="rotate" values="-5 36 40;5 36 40;-5 36 40" dur="2s" repeatCount="indefinite" />
      <line x1="30" y1="36" x2="42" y2="44" stroke="url(#pa-g)" strokeWidth="2" strokeLinecap="round" />
    </g>
  </svg>
);

const ShieldIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="sh-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M26 10 L38 15 L38 26 C38 33 32 39 26 42 C20 39 14 33 14 26 L14 15 Z"
      stroke="url(#sh-g)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-dasharray" values="0,150;150,0;150,0" dur="3s" repeatCount="indefinite" />
    </path>
    <path d="M20 26 L24 30 L32 22" stroke="url(#sh-g)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <animate attributeName="stroke-dasharray" values="0,30;30,0" dur="1.5s" begin="1s" repeatCount="indefinite" />
    </path>
    <circle cx="26" cy="26" r="18" stroke="url(#sh-g)" strokeWidth="0.8" fill="none" opacity="0.3">
      <animate attributeName="r" values="18;22;18" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const GlobeIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="gl-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <circle cx="26" cy="26" r="16" stroke="url(#gl-g)" strokeWidth="1.5" fill="none" />
    <ellipse cx="26" cy="26" rx="8" ry="16" stroke="url(#gl-g)" strokeWidth="1" fill="none" opacity="0.6">
      <animateTransform attributeName="transform" type="rotate" values="0 26 26;360 26 26" dur="8s" repeatCount="indefinite" />
    </ellipse>
    <line x1="10" y1="26" x2="42" y2="26" stroke="url(#gl-g)" strokeWidth="1" opacity="0.5" />
    <ellipse cx="26" cy="26" rx="16" ry="6" stroke="url(#gl-g)" strokeWidth="1" fill="none" opacity="0.4" />
    <circle cx="26" cy="14" r="2" fill="url(#gl-g)">
      <animate attributeName="opacity" values="1;0.2;1" dur="1.5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const LightbulbIcon = ({ c1, c2 }: { c1: string; c2: string }) => {
  const rays = Array.from({ length: 8 }, (_, i) => {
    const angle = i * 45 * Math.PI / 180;
    return {
      x1: 26 + Math.cos(angle)*18, y1: 24 + Math.sin(angle)*18,
      x2: 26 + Math.cos(angle)*22, y2: 24 + Math.sin(angle)*22,
      x2far: 26 + Math.cos(angle)*25, y2far: 24 + Math.sin(angle)*25,
    };
  });
  return (
    <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
      <defs>
        <linearGradient id="lb-g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
        </linearGradient>
      </defs>
      {rays.map((r, i) => (
        <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} stroke="url(#lb-g)" strokeWidth="1.2" strokeLinecap="round" opacity="0.5">
          <animate attributeName="opacity" values="0.2;0.8;0.2" dur="2s" begin={`${i*0.2}s`} repeatCount="indefinite" />
          <animate attributeName="x2" values={`${r.x2};${r.x2far};${r.x2}`} dur="2s" begin={`${i*0.2}s`} repeatCount="indefinite" />
          <animate attributeName="y2" values={`${r.y2};${r.y2far};${r.y2}`} dur="2s" begin={`${i*0.2}s`} repeatCount="indefinite" />
        </line>
      ))}
      <path d="M20 32 C18 30 16 28 16 24 C16 19 20.5 14 26 14 C31.5 14 36 19 36 24 C36 28 34 30 32 32 L32 36 L20 36 Z"
        stroke="url(#lb-g)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="22" y1="38" x2="30" y2="38" stroke="url(#lb-g)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="23" y1="40.5" x2="29" y2="40.5" stroke="url(#lb-g)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="26" cy="24" r="4" fill="url(#lb-g)" opacity="0.3">
        <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

const HardDriveIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="hd-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    {([13,23,33] as number[]).map((y,i) => (
      <g key={i}>
        <rect x="12" y={y} width="28" height="8" rx="2" stroke="url(#hd-g)" strokeWidth="1.5" fill="none" />
        <circle cx="35" cy={y+4} r="2" fill="url(#hd-g)">
          <animate attributeName="fill" values={`${c1};${c2};${c1}`} dur="1.5s" begin={`${i*0.5}s`} repeatCount="indefinite" />
          <animate attributeName="r" values="2;2.8;2" dur="1.5s" begin={`${i*0.5}s`} repeatCount="indefinite" />
        </circle>
        <line x1="16" y1={y+4} x2="16" y2={y+4} stroke="url(#hd-g)" strokeWidth="1" opacity="0.5">
          <animate attributeName="x2" values="16;28;16" dur={`${1.5+i*0.2}s`} begin={`${i*0.3}s`} repeatCount="indefinite" />
        </line>
      </g>
    ))}
  </svg>
);

const VoIPIcon = ({ c1, c2 }: { c1: string; c2: string }) => (
  <svg width="42" height="42" viewBox="0 0 52 52" fill="none">
    <defs>
      <linearGradient id="ph-g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
      </linearGradient>
    </defs>
    <path d="M14 28 C14 18 20 11 26 11 C32 11 38 18 38 28"
      stroke="url(#ph-g)" strokeWidth="1.8" fill="none" strokeLinecap="round">
      <animate attributeName="stroke-dasharray" values="0,80;80,0;80,0" dur="2.5s" repeatCount="indefinite" />
    </path>
    <rect x="10" y="26" width="7" height="11" rx="3.5" stroke="url(#ph-g)" strokeWidth="1.5" fill="none">
      <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" begin="0s" repeatCount="indefinite" />
    </rect>
    <rect x="35" y="26" width="7" height="11" rx="3.5" stroke="url(#ph-g)" strokeWidth="1.5" fill="none">
      <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" begin="0.3s" repeatCount="indefinite" />
    </rect>
    <path d="M17 37 C17 40 20 43 26 43" stroke="url(#ph-g)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <circle cx="26" cy="43" r="2.2" stroke="url(#ph-g)" strokeWidth="1.5" fill="none">
      <animate attributeName="r" values="2.2;3.2;2.2" dur="1.2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.6;1;0.6" dur="1.2s" repeatCount="indefinite" />
    </circle>
    {([5,9] as number[]).map((r,i) => (
      <path key={i} d={`M${26-r} 43 Q${26} ${43-r} ${26+r} 43`}
        stroke="url(#ph-g)" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.5">
        <animate attributeName="opacity" values="0;0.7;0" dur="1.5s" begin={`${i*0.4}s`} repeatCount="indefinite" />
        <animate attributeName="d"
          values={`M${26-r} 43 Q${26} ${43-r} ${26+r} 43;M${26-r-2} 43 Q${26} ${43-r-3} ${26+r+2} 43;M${26-r} 43 Q${26} ${43-r} ${26+r} 43`}
          dur="1.5s" begin={`${i*0.4}s`} repeatCount="indefinite" />
      </path>
    ))}
    {([20,26,32] as number[]).map((x,i) => (
      <circle key={i} cx={x} cy="7" r="1.5" fill="url(#ph-g)">
        <animate attributeName="opacity" values="0;1;0" dur="1.8s" begin={`${i*0.35}s`} repeatCount="indefinite" />
        <animate attributeName="cy" values="7;5;7" dur="1.8s" begin={`${i*0.35}s`} repeatCount="indefinite" />
      </circle>
    ))}
  </svg>
);

const ICON_COMPONENTS = [
  AIIcon, CloudIcon, CodeIcon, CartIcon, TargetIcon, ServerIcon,
  PaletteIcon, ShieldIcon, GlobeIcon, LightbulbIcon, HardDriveIcon, VoIPIcon,
];

const capabilities = [
  { id: 1,  title: 'AI Solutions',           IconComp: ICON_COMPONENTS[0],  theme: CARD_THEMES[0],  image: AISolutions,                slug: 'ai-solutions',           category: 'technology'     },
  { id: 2,  title: 'Cloud Solutions',        IconComp: ICON_COMPONENTS[1],  theme: CARD_THEMES[1],  image: CloudSolutions,             slug: 'cloud-solutions',        category: 'infrastructure' },
  { id: 3,  title: 'App Development',        IconComp: ICON_COMPONENTS[2],  theme: CARD_THEMES[2],  image: CustomSoftwareDevelopment,  slug: 'application-development',               category: 'development'    },
  { id: 4,  title: 'E-commerce Solutions',   IconComp: ICON_COMPONENTS[3],  theme: CARD_THEMES[3],  image: EcommerceSolutions,         slug: 'e-commerce-solutions',   category: 'development'    },
  { id: 5,  title: 'Marketing',              IconComp: ICON_COMPONENTS[4],  theme: CARD_THEMES[4],  image: SEO,                        slug: 'marketing',              category: 'marketing'      },
  { id: 6,  title: 'IT Management',          IconComp: ICON_COMPONENTS[5],  theme: CARD_THEMES[5],  image: ITInfrastructureManagement, slug: 'it-service-package',     category: 'infrastructure' },
  { id: 7,  title: 'UI/UX Design',           IconComp: ICON_COMPONENTS[6],  theme: CARD_THEMES[6],  image: UIUX,                       slug: 'ui-ux-design',           category: 'design'         },
  { id: 8,  title: 'Cybersecurity Services', IconComp: ICON_COMPONENTS[7],  theme: CARD_THEMES[7],  image: Cybersecurity,              slug: 'cybersecurity-services', category: 'infrastructure' },
  { id: 9,  title: 'Web Hosting & Domain',   IconComp: ICON_COMPONENTS[8],  theme: CARD_THEMES[8],  image: WebHosting,                 slug: 'domains',                category: 'infrastructure' },
  { id: 10, title: 'IT Consulting',          IconComp: ICON_COMPONENTS[9],  theme: CARD_THEMES[9],  image: ITConsulting,               slug: 'it-consulting',          category: 'technology'     },
  { id: 11, title: 'NAS Solutions',          IconComp: ICON_COMPONENTS[10], theme: CARD_THEMES[10], image: NASSolutions,               slug: 'nas',          category: 'infrastructure' },
  { id: 12, title: 'VoIP Services',          IconComp: ICON_COMPONENTS[11], theme: CARD_THEMES[11], image: VoipService,                slug: 'voip-service',          category: 'infrastructure' },
];

/* ─── Tile Card ─────────────────────────────────────────── */
const ServiceTile = ({ item }: { item: typeof capabilities[0] }) => {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const { IconComp, theme } = item;

  const cardBorderStyle = useMemo(() => ({
    border: hovered
      ? `1px solid ${theme.gradientFrom}50`
      : '1px solid rgba(255,255,255,0.08)',
    boxShadow: hovered
      ? `0 16px 48px -12px ${theme.gradientFrom}30`
      : 'none',
  }), [hovered, theme]);

  const cardBgStyle = useMemo(() => ({
    background: 'rgba(255,255,255,0.04)',
  }), []);

  const dotGridStyle = useMemo(() => ({
    backgroundImage: `radial-gradient(circle, ${theme.gradientFrom}20 1px, transparent 1px)`,
    backgroundSize: '18px 18px',
    opacity: 0.4,
  }), [theme]);

  return (
    <div
      className="cap-tile group relative cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(`/${item.slug}`)}
    >
      {/* Outer glow ring */}
      <div
        className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${theme.gradientFrom}60, ${theme.gradientTo}30)`,
          filter: 'blur(1px)',
        }}
      />

      {/* Card body — fixed height, nothing ever changes size */}
      <div
        className="relative rounded-2xl overflow-hidden transition-all duration-500"
        style={{ height: '162px', ...cardBgStyle, ...cardBorderStyle }}
      >
        {/* Dot-grid texture */}
        <div className="absolute inset-0 pointer-events-none rounded-2xl" style={dotGridStyle} />

        {/* ── Full-card image (absolute, covers everything) ── */}
        <div
          className="absolute inset-0"
          style={{
            transition: 'opacity 0.55s ease, transform 0.55s ease',
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'scale(1)' : 'scale(1.08)',
            zIndex: 1,
          }}
        >
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${theme.gradientFrom}30, ${theme.gradientTo}18)` }} />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(105deg, transparent 35%, ${theme.gradientFrom}35 50%, transparent 65%)`,
              animation: hovered ? 'cap-shimmer-sweep 0.7s ease-out forwards' : 'none',
            }}
          />
        </div>

        {/* ── Icon (absolute centre, fades out on hover) ── */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transition: 'opacity 0.45s ease, transform 0.45s ease',
            opacity: hovered ? 0 : 1,
            transform: hovered ? 'scale(0.82) translateY(-10px)' : 'scale(1) translateY(0)',
            background: `radial-gradient(ellipse at 50% 45%, ${theme.gradientFrom}18 0%, transparent 70%)`,
            zIndex: 2,
            paddingBottom: '36px', /* push icon up to leave room for title */
          }}
        >
          <div
            className="p-2 rounded-xl"
            style={{
              background: `linear-gradient(135deg, ${theme.gradientFrom}22, ${theme.gradientTo}12)`,
              border: `1px solid ${theme.gradientFrom}40`,
              boxShadow: `0 0 28px ${theme.gradientFrom}35, inset 0 0 16px ${theme.gradientFrom}12`,
            }}
          >
            <IconComp c1={theme.gradientFrom} c2={theme.gradientTo} />
          </div>
        </div>

        {/* ── Title (absolute bottom, always visible) ── */}
        <div
          className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 px-4 py-3.5"
          style={{ zIndex: 3 }}
        >
          <span
            className="font-semibold tracking-tight transition-all duration-500 leading-tight text-center"
            style={{
              background: hovered ? `linear-gradient(90deg, ${theme.gradientFrom}, ${theme.gradientTo})` : 'none',
              WebkitBackgroundClip: hovered ? 'text' : 'unset',
              WebkitTextFillColor: hovered ? 'transparent' : '#F1F5F9',
              opacity: hovered ? 0.72 : 1,
            }}
          >
            {item.title}
          </span>
          <ArrowRight
            className="w-3.5 h-3.5 flex-shrink-0 transition-all duration-500 group-hover:translate-x-0.5"
            style={{
              color: hovered ? theme.gradientTo : '#b9b9b9',
              opacity: hovered ? 0.72 : 1,
            }}
          />
        </div>

        {/* Bottom accent bar */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
          style={{ background: theme.bar, zIndex: 4 }}
        />
      </div>
    </div>
  );
};

/* ─── Section ────────────────────────────────────────────── */
const CapabilitiesSection = () => {
  const filtered = useMemo(() => capabilities, []);

  return (
    <section
      className="relative py-14 md:py-16 md:px-24 overflow-hidden"
      id="capabilities"
      style={{ scrollMarginTop: '100px' }}
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-48 -right-48 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-48 w-[400px] h-[400px] bg-sky-500/8 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 w-80 h-80 bg-emerald-500/8 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 mb-7 bg-white/5 rounded-full border border-white/10">
            <Bolt className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-semibold text-gray-300 tracking-widest uppercase">Our Expertise</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight tracking-tight">
            Comprehensive Digital
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent pl-2">
              Capabilities
            </span>
          </h2>
        </div>

        {/* Grid — 1 col mobile → 2 sm → 3 md → 4 lg */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {filtered.map(item => (
            <ServiceTile key={item.id} item={item} />
          ))}
        </div>
      </div>

      <style>{`
        #capabilities { contain: layout style paint; }
        .cap-tile * { -webkit-font-smoothing: antialiased; }

        @keyframes cap-shimmer-sweep {
          0%   { transform: translateX(-120%); opacity: 1; }
          100% { transform: translateX(220%);  opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default CapabilitiesSection;